import { Link } from 'react-router-dom';
import { assetUrl } from '../../config/constants';

const LAST_UPDATED = 'July 2026';

const AUDIO_LANGS = [
  'Spanish',
  'French',
  'German',
  'Italian',
  'Dutch',
  'Portuguese',
  'Russian',
  'Arabic',
  'Hindi',
  'Tamil',
  'Kannada',
  'Malayalam',
  'Polish',
  'Chinese',
];

export default function CreditsPage() {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="mx-auto max-w-5xl">
        <div className="mb-10">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-black text-slate-900">Credits</h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
          <p className="mt-4 max-w-3xl text-slate-600">
            SublingoX uses open educational resources and third-party services. This page lists the
            public audio sources used for Real Voice Accent Practice and other project credits.
          </p>
        </div>

        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Audio Recordings</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Accent-practice recordings for the languages below are sourced from Wikimedia Commons files
              released under Creative Commons Zero 1.0 Universal (CC0-1.0). CC0 does not require attribution,
              but we credit Wikimedia Commons here as the public source of these recordings.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Older local Telugu and English audio tracks may include recordings sourced from LinguaLibre.org.
              Lingua Libre recordings commonly use CC BY-SA 4.0, which requires attribution and share-alike
              treatment when redistributed or adapted.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-200 p-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Wikimedia Commons CC0 Audio</h2>
                <p className="mt-1 text-sm text-slate-500">{AUDIO_LANGS.length} language audio sets</p>
              </div>
              <a
                href="https://creativecommons.org/publicdomain/zero/1.0/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                CC0 1.0 license
              </a>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {AUDIO_LANGS.map((lang) => (
                  <span
                    key={lang}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-600"
                  >
                    {lang}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-sm leading-relaxed text-slate-600">
                Source: Wikimedia Commons, a public media repository hosted by the Wikimedia Foundation.
                Individual audio URLs are stored directly in each language&apos;s audio index file.
              </p>
              <a
                href="https://commons.wikimedia.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Visit Wikimedia Commons
              </a>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Alphabet and Character Data</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Alphabet repertoires and character names for supported languages are derived from Unicode
              CLDR 48.2 and Unicode 17.0.0 data files. Unicode data files are distributed under the
              Unicode License v3.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm font-semibold">
              <a
                href="https://cldr.unicode.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Unicode CLDR
              </a>
              <a
                href={assetUrl('lang-data-source/licenses/UNICODE-LICENSE.txt')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Unicode License v3
              </a>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Pronunciation Fallbacks</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Missing cross-script pronunciation guides are generated with ICU transliteration while
              existing hand-authored guides are preserved. These automated reading aids are approximate
              and should receive native-speaker review before being treated as phonetic authority.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Vocabulary Translations</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Vocabulary translations for newly supported languages are primarily derived from the English
              Wiktionary data extracted by Kaikki.org under CC BY-SA 4.0. Missing dictionary entries use
              automated translation and are marked in each dataset as requiring native-speaker review.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm font-semibold">
              <a
                href="https://kaikki.org/dictionary/English/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Kaikki / English Wiktionary data
              </a>
              <a
                href="https://creativecommons.org/licenses/by-sa/4.0/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                CC BY-SA 4.0
              </a>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Conversation Translations</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Missing conversation chapters for newly supported languages were automatically translated
              from SublingoX&apos;s English conversation lessons. Generated chapter files are marked as
              automated imports and should receive native-speaker review as the content evolves.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Grammar Guides</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Language-specific learner guides cover noun reference, parts of speech, sentence structure,
              and verb tense or aspect across every supported language. Languages without English-style
              articles or tense conjugation are described using their own classifiers, particles, aspect
              markers, agreement, and word-order systems. Newly structured guides are marked for
              native-speaker review in their dataset metadata.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Generated Exercise Packs</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Beginner-to-medium exercise packs are generated locally from SublingoX&apos;s localized basics,
              vocabulary, and grammar datasets. The build does not send lesson content to an external
              service. Generated packs include source metadata and are marked for native-speaker review.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Essential Vocabulary Expansion</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Health, daily-routine, and technology vocabulary is translated with Google Translate from a
              context-scoped English learner list. These generated entries are marked as automated translations
              and should receive native-speaker review before being treated as authoritative dictionary content.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Speaking and Reading Packs</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Step 10 speaking packs are generated locally from the project&apos;s localized basics and vocabulary.
              Text-to-speech and speech recognition are provided by the user&apos;s browser or operating system;
              recognized text is scored on the device by the app and is not stored by SublingoX.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Short-Story Adaptations</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              For languages without dedicated prose stories, localized conversation lessons are adapted
              into graded dialogue-reading stories. These adaptations are created locally from SublingoX
              content and do not send story text to an external service.
            </p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800">Other Credits</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Authentication and cloud services are provided through Firebase/Google services where enabled.
              Browser text-to-speech playback is provided by the user&apos;s browser or operating system.
            </p>
          </section>
        </div>

        <div className="mt-10 flex flex-col items-center gap-2 text-center text-xs text-slate-400">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/terms" className="hover:text-indigo-600 hover:underline">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-indigo-600 hover:underline">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-indigo-600 hover:underline">Contact</Link>
            <Link to="/" className="hover:text-indigo-600 hover:underline">Home</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SublingoX · Subram Miles</p>
        </div>
      </div>
    </div>
  );
}
